// ===========================================
// Email Unsubscribe API (PUBLIC — magic link)
// ===========================================
// Mirrors `/public/email-unsubscribe` (backend `emailUnsubscribePublic.routes.ts`).
// Uses `publicClient`, NOT `apiClient`: the recipient is typically a login-less
// contact 2, so there is no bearer token to attach and no 401 interceptor that
// should ever fire here.
//
// ⚠️ GET IS READ-ONLY. Do not add a "record the click" call to `get()`. Mail
// scanners fetch every url in an email within seconds of delivery — a mutating
// GET unsubscribes families who never clicked, and the resulting row is
// indistinguishable from a real opt-out afterwards.

import { publicClient } from '../services/api';

/** Matches the local declaration in `permissionSlips.api.ts` — the sibling public API. */
interface ApiResponse<T> {
  status: string;
  data: T;
}

/**
 * School chrome for the confirm page.
 *
 * The recipient arrives from an email with no portal shell and no login — the
 * name and logo are what distinguish a real page from a phishing form.
 */
export interface UnsubscribeSchool {
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
}

export interface UnsubscribeDetails {
  school: UnsubscribeSchool;
  /**
   * MASKED, e.g. `p•••••@e••••••.com` — the bullet runs are length-proportional,
   * so first char, domain TLD and lengths survive (enough to recognise your own
   * address) and nothing else does. A malformed address masks to `•••`.
   *
   * The server never returns the full address, nor any family/parent/student
   * name: the token proves possession of an inbox, not identity, and links get
   * forwarded.
   */
  maskedEmail: string;
  scope: string;
  /** Human-readable, e.g. "class reminder emails". Rendered as-is. */
  scopeLabel: string;
  alreadyUnsubscribed: boolean;
}

export const emailUnsubscribeApi = {
  /** Open the confirm page. Read-only — see the file header. */
  get: (token: string) =>
    publicClient
      .get<ApiResponse<UnsubscribeDetails>>(
        `/public/email-unsubscribe/${encodeURIComponent(token)}`
      )
      .then((r) => r.data),

  /**
   * Perform the opt-out.
   *
   * Idempotent — a repeat returns 200, never 409, because mail clients POSTing
   * the RFC 8058 one-click header and a parent double-tapping on a phone both
   * produce repeats. Only the first caller's audit trail is recorded.
   */
  unsubscribe: (token: string) =>
    publicClient
      .post<ApiResponse<UnsubscribeDetails>>(
        `/public/email-unsubscribe/${encodeURIComponent(token)}`
      )
      .then((r) => r.data),
};
